// Compare Mode using MapLibre — 3 modes
import { useEffect, useRef, useState } from 'react';
import maplibregl, { Map as MaplibreMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { fetchGeoJSON } from '@/utils/geojsonCache';

interface CompareMode {
  type: 'slider' | 'overlay' | 'side_by_side';
  oldLayers: Array<{ id: string; geojsonKey: string; name: string; style: any }>;
  newLayers: Array<{ id: string; geojsonKey: string; name: string; style: any }>;
}

interface CompareViewProps {
  mode: CompareMode;
  onClose: () => void;
}

const OSM_STYLE: any = {
  version: 8,
  sources: {
    'osm-raster': {
      type: 'raster',
      tiles: [
        'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
        'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
    },
  },
  layers: [
    { id: 'osm-tiles', type: 'raster', source: 'osm-raster' },
  ],
};

export function CompareView({ mode, onClose }: CompareViewProps) {
  if (mode.type === 'side_by_side') return <SideBySide mode={mode} onClose={onClose} />;
  if (mode.type === 'slider') return <SliderCompare mode={mode} onClose={onClose} />;
  return <OverlayCompare mode={mode} onClose={onClose} />;
}

// =========== SLIDER COMPARE ===========
function SliderCompare({ mode, onClose }: CompareViewProps) {
  const [position, setPosition] = useState(50);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const leftMap = useRef<MaplibreMap | null>(null);
  const rightMap = useRef<MaplibreMap | null>(null);
  const draggingRef = useRef(false);

  useEffect(() => {
    if (!leftRef.current || !rightRef.current) return;

    leftMap.current = new maplibregl.Map({
      container: leftRef.current,
      style: OSM_STYLE,
      center: [105.8542, 21.0285],
      zoom: 15,
    });
    rightMap.current = new maplibregl.Map({
      container: rightRef.current,
      style: OSM_STYLE,
      center: [105.8542, 21.0285],
      zoom: 15,
    });

    let syncing = false;
    const sync = (from: MaplibreMap, to: MaplibreMap) => {
      if (syncing) return;
      syncing = true;
      to.setCenter(from.getCenter());
      to.setZoom(from.getZoom());
      requestAnimationFrame(() => { syncing = false; });
    };

    leftMap.current.on('move', () => sync(leftMap.current!, rightMap.current!));
    rightMap.current.on('move', () => sync(rightMap.current!, leftMap.current!));

    // Load layers
    leftMap.current.on('load', () => {
      loadLayers(leftMap.current!, mode.oldLayers, '#3b82f6', 'old');
    });
    rightMap.current.on('load', () => {
      loadLayers(rightMap.current!, mode.newLayers, '#ef4444', 'new');
    });

    return () => {
      leftMap.current?.remove();
      rightMap.current?.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handlePointerDown(e: React.PointerEvent) {
    draggingRef.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }
  function handlePointerMove(e: React.PointerEvent) {
    if (!draggingRef.current) return;
    const container = (e.currentTarget as HTMLElement).parentElement!;
    const rect = container.getBoundingClientRect();
    const pct = Math.max(5, Math.min(95, ((e.clientX - rect.left) / rect.width) * 100));
    setPosition(pct);
  }
  function handlePointerUp() { draggingRef.current = false; }

  return (
    <div className="fixed inset-0 z-50 bg-black">
      <div className="absolute top-4 left-4 right-4 z-10 flex justify-between items-center">
        <div className="flex gap-2">
          <span className="bg-blue-600 text-white px-3 py-1 rounded text-sm font-medium">CŨ</span>
          <span className="bg-red-600 text-white px-3 py-1 rounded text-sm font-medium">MỚI</span>
        </div>
        <button onClick={onClose} className="bg-white text-gray-900 px-4 py-1.5 rounded text-sm">✕ Thoát</button>
      </div>
      <div className="absolute inset-0 flex">
        <div ref={leftRef} className="flex-1" style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }} />
        <div ref={rightRef} className="flex-1" style={{ clipPath: `inset(0 0 0 ${position}%)` }} />
      </div>
      <div
        className="absolute top-0 bottom-0 z-10 cursor-ew-resize"
        style={{ left: `${position}%`, width: '4px', transform: 'translateX(-50%)', background: 'white' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center text-gray-700 font-bold">
          ‖
        </div>
      </div>
    </div>
  );
}

// =========== OVERLAY COMPARE ===========
function OverlayCompare({ mode, onClose }: CompareViewProps) {
  const [oldOpacity, setOldOpacity] = useState(0.5);
  const [newOpacity, setNewOpacity] = useState(0.7);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<MaplibreMap | null>(null);

  useEffect(() => {
    if (!mapRef.current) return;
    mapInstance.current = new maplibregl.Map({
      container: mapRef.current,
      style: OSM_STYLE,
      center: [105.8542, 21.0285],
      zoom: 15,
    });
    mapInstance.current.on('load', () => {
      loadLayers(mapInstance.current!, mode.oldLayers, '#3b82f6', 'old');
      loadLayers(mapInstance.current!, mode.newLayers, '#ef4444', 'new');
    });
    return () => { mapInstance.current?.remove(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mapInstance.current) return;
    ['old-line', 'old-fill', 'new-line', 'new-fill'].forEach((id) => {
      if (!mapInstance.current!.getLayer(id)) return;
      const prefix = id.startsWith('old-') ? 'old' : 'new';
      const opacity = prefix === 'old' ? oldOpacity : newOpacity;
      if (id.endsWith('-line')) {
        mapInstance.current!.setPaintProperty(id, 'line-opacity', opacity);
      } else {
        mapInstance.current!.setPaintProperty(id, 'fill-opacity', opacity * 0.3);
      }
    });
  }, [oldOpacity, newOpacity]);

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      <div className="absolute top-4 left-4 right-4 z-10 flex justify-between items-center">
        <div className="bg-white/90 backdrop-blur rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <span className="w-3 h-3 rounded-sm bg-blue-500"></span>
            <span className="text-gray-700">Cũ</span>
            <input type="range" min={0} max={1} step={0.05} value={oldOpacity} onChange={(e) => setOldOpacity(parseFloat(e.target.value))} className="w-32" />
            <span className="text-xs text-gray-500 w-10">{(oldOpacity * 100).toFixed(0)}%</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="w-3 h-3 rounded-sm bg-red-500"></span>
            <span className="text-gray-700">Mới</span>
            <input type="range" min={0} max={1} step={0.05} value={newOpacity} onChange={(e) => setNewOpacity(parseFloat(e.target.value))} className="w-32" />
            <span className="text-xs text-gray-500 w-10">{(newOpacity * 100).toFixed(0)}%</span>
          </div>
        </div>
        <button onClick={onClose} className="bg-white text-gray-900 px-4 py-1.5 rounded text-sm">✕ Thoát</button>
      </div>
      <div ref={mapRef} className="flex-1" />
    </div>
  );
}

// =========== SIDE-BY-SIDE ===========
function SideBySide({ mode, onClose }: CompareViewProps) {
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const leftMap = useRef<MaplibreMap | null>(null);
  const rightMap = useRef<MaplibreMap | null>(null);

  useEffect(() => {
    if (!leftRef.current || !rightRef.current) return;
    leftMap.current = new maplibregl.Map({ container: leftRef.current, style: OSM_STYLE, center: [105.8542, 21.0285], zoom: 15 });
    rightMap.current = new maplibregl.Map({ container: rightRef.current, style: OSM_STYLE, center: [105.8542, 21.0285], zoom: 15 });

    let syncing = false;
    const sync = (from: MaplibreMap, to: MaplibreMap) => {
      if (syncing) return;
      syncing = true;
      to.setCenter(from.getCenter());
      to.setZoom(from.getZoom());
      requestAnimationFrame(() => { syncing = false; });
    };
    leftMap.current.on('move', () => sync(leftMap.current!, rightMap.current!));
    rightMap.current.on('move', () => sync(rightMap.current!, leftMap.current!));

    leftMap.current.on('load', () => loadLayers(leftMap.current!, mode.oldLayers, '#3b82f6', 'old'));
    rightMap.current.on('load', () => loadLayers(rightMap.current!, mode.newLayers, '#ef4444', 'new'));

    return () => {
      leftMap.current?.remove();
      rightMap.current?.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      <div className="absolute top-4 left-4 right-4 z-10 flex justify-between items-center">
        <div className="flex gap-2">
          <span className="bg-blue-600 text-white px-3 py-1 rounded text-sm font-medium">CŨ</span>
          <span className="bg-red-600 text-white px-3 py-1 rounded text-sm font-medium">MỚI</span>
        </div>
        <button onClick={onClose} className="bg-white text-gray-900 px-4 py-1.5 rounded text-sm">✕ Thoát</button>
      </div>
      <div className="flex-1 grid grid-cols-2 gap-0">
        <div ref={leftRef} />
        <div ref={rightRef} />
      </div>
    </div>
  );
}

async function loadLayers(map: MaplibreMap, layers: Array<{ id: string }>, color: string, prefix: string) {
  for (const l of layers) {
    try {
      const url = `/api/layers/${l.id}/geojson`;
      const { data: geojson } = await fetchGeoJSON(url);
      const sourceId = `${prefix}-src-${l.id}`;
      const lineLayerId = `${prefix}-line`;
      const fillLayerId = `${prefix}-fill`;
      if (!map.getSource(sourceId)) {
        map.addSource(sourceId, { type: 'geojson', data: geojson as any });
      }
      if (!map.getLayer(lineLayerId)) {
        map.addLayer({ id: lineLayerId, type: 'line', source: sourceId, paint: { 'line-color': color, 'line-width': 2, 'line-opacity': 0.8 } });
      }
      if (!map.getLayer(fillLayerId)) {
        map.addLayer({ id: fillLayerId, type: 'fill', source: sourceId, paint: { 'fill-color': color, 'fill-opacity': 0.3 } });
      }
    } catch (err) {
      console.error(`Failed to load layer ${l.id}:`, err);
    }
  }
}
